import { useFormik } from "formik";
import { toast } from "react-toastify";
import { toFormikValidationSchema } from "zod-formik-adapter";
import {
  createLocationNoteDtoSchema,
  useAdminLocationNotesControllerCreate,
  useAdminLocationNotesControllerDelete,
  useAdminLocationNotesControllerFindAll,
  useAdminLocationNotesControllerUpdate,
} from "service";
import {
  CreateLocationNoteDto,
  UpdateLocationNoteDto,
} from "service/api/estabrookSchemas";
import { useDialogStore } from "../state/dialog-store";

interface UseLocationNotesOptions {
  locationId: string;
  onCreateSuccess?: () => void;
  onDeleteSuccess?: () => void;
  onUpdateSuccess?: () => void;
  enabled?: boolean;
  refetchOptions?: {
    refetchOnWindowFocus?: boolean;
    refetchOnMount?: boolean;
    staleTime?: number;
  };
}

export const useLocationNotes = ({
  locationId,
  onCreateSuccess,
  onDeleteSuccess,
  onUpdateSuccess,
  enabled = true,
  refetchOptions = {},
}: UseLocationNotesOptions) => {
  const { setDialog } = useDialogStore();

  // Fetch notes
  const { data, refetch, isLoading } = useAdminLocationNotesControllerFindAll(
    {
      pathParams: {
        locationId,
      },
    },
    {
      enabled: enabled && !!locationId,
      ...refetchOptions,
    },
  );

  // Formik setup
  const {
    values,
    handleBlur,
    handleChange,
    touched,
    errors,
    resetForm,
    isValid,
  } = useFormik<CreateLocationNoteDto>({
    initialValues: {
      content: "",
      mustRead: false,
    },
    validationSchema: toFormikValidationSchema(createLocationNoteDtoSchema),
    onSubmit: (values) => {
      if (values.content.trim()) {
        createNote({
          body: values,
          pathParams: {
            locationId,
          },
        });
      }
    },
  });

  // Create note mutation
  const { mutate: createNote } = useAdminLocationNotesControllerCreate({
    onSuccess: async () => {
      resetForm();
      toast.success("Note Published");
      await refetch();
      onCreateSuccess?.();
    },
  });

  // Delete note mutation
  const { mutate: deleteNote } = useAdminLocationNotesControllerDelete({
    onSuccess: async () => {
      toast.success("Note Deleted");
      await refetch();
      onDeleteSuccess?.();
    },
  });

  // Update note mutation
  const { mutate: editNote, isLoading: isEditNotePending } =
    useAdminLocationNotesControllerUpdate({
      onSuccess: async (_, variables) => {
        if (variables.body?.mustRead !== undefined) {
          toast.success(
            `Note ${variables.body?.mustRead ? "Pinned" : "Unpinned"} To Driver App`,
          );
        } else {
          toast.success("Note Edited");
        }

        await refetch();
        onUpdateSuccess?.();
      },
    });

  // Submit form with validation
  const submitForm = () => {
    if (!isValid || !values.content.trim()) {
      return;
    }

    createNote({
      body: values,
      pathParams: {
        locationId,
      },
    });
  };

  // Internal function to actually delete the note
  const performDelete = (noteId: string) => {
    deleteNote({
      pathParams: {
        locationNoteId: noteId,
      },
    });
  };

  // Handle delete note with confirmation
  const handleDeleteNote = (noteId: string) => {
    setDialog({
      title: "Delete Note",
      description: "Are you sure you want to delete this note?",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async (onClose: () => void) => {
        performDelete(noteId);
        onClose();
      },
    });
  };

  // Handle edit note
  const handleEditNote = (
    noteId: string,
    updateNotePayload: UpdateLocationNoteDto,
  ) => {
    editNote({
      pathParams: {
        locationId,
        locationNoteId: noteId,
      },
      body: updateNotePayload,
    });
  };

  return {
    // Data
    data,
    isLoading,
    refetch,

    // Form state
    values,
    handleBlur,
    handleChange,
    touched,
    errors,
    resetForm,
    isValid,

    // Actions
    submitForm,
    handleDeleteNote,
    handleEditNote,
    isEditNotePending,

    // Raw mutations (if needed for custom logic)
    createNote,
    deleteNote,
    editNote,
  };
};
